import React from 'react';
import { handle400Statuses, sendApiRequest } from '@services/api';
import { logger } from '@utils/usefulFuncs';
import { useDispatch, useSelector } from 'react-redux';
import { uploadStatusActions } from '@constants/actionTypes';
import { createTimeInstance } from '@utils/time';

export function isUrlIngestionComplete(url, completed) {
  let isComplete = false;
  completed.forEach((item) => {
    const { title } = item;
    if (title.includes(url)) {
      isComplete = true;
    }
  });
  return isComplete;
}

export function convertUrlToIngestionCompletedDocument(urls, completed) {
  const docs = [];
  urls.forEach((url) => {
    const ingestionData = completed.find((item) => item.title.includes(url));
    if (ingestionData) {
      docs.push(ingestionData);
    } else {
      logger('ingestion data was missing', { urls, completed });
    }
  });
  return docs;
}

function groupData(data) {
  const completed = [];
  const pending = [];
  const errored = [];
  data.forEach((d) => {
    const { title, status } = d;
    if (status === 'Completed') {
      completed.push(d);
    } else if (status === 'In progress') {
      pending.push(d);
    } else if (status === 'Errored') {
      errored.push(d);
    }
  });
  return { completed, pending, errored };
}

const POLL_DELAY = 5000;

export async function fetchUploadStatus(filter) {
  const URL = `v1/genai_user_crawl_jobs_get`;
  if (filter) {
    URL = addFilter(URL, filter);
  }
  const { status, data, error } = await sendApiRequest('get', URL);
  handle400Statuses(status);
  if (status === 200) {
    const groupedData = groupData(data);
    return {
      status,
      groupedData,
    };
  } else {
    return {
      status,
      error,
    };
  }
}

function addFilter(URL, filter) {
  return URL + `?${filter}=true`;
}

export async function fetchAndUpdateStoreUploadStatus(dispatch, filter) {
  const timeStart = createTimeInstance();

  let URL = `v1/genai_user_crawl_jobs_get`;
  if (filter) {
    URL = addFilter(URL, filter);
  }
  const { status, data } = await sendApiRequest('get', URL);
  handle400Statuses(status);
  if (status === 200) {
    const groupedData = groupData(data);
    dispatch({
      type: uploadStatusActions.receiveData,
      payload: {
        timeStart,
        data: groupedData,
      },
    });
  }
}

export async function fetchAndUpdateStoreTemplateGenerationUploadStatus(dispatch) {
  const timeStart = createTimeInstance();
  const URL = `v1/genai/templates/status`;
  const { status, data } = await sendApiRequest('get', URL);
  handle400Statuses(status);
  if (status === 200) {
    const groupedData = groupData(data);
    dispatch({
      type: uploadStatusActions.receiveData,
      payload: {
        timeStart,
        data: groupedData,
      },
    });
  }
}

export async function fetchAndUpdateStoreDDQTemplateGenerationUploadStatus(dispatch) {
  const timeStart = createTimeInstance();
  const URL = `v1/genai/templates/status`;
  const { status, data } = await sendApiRequest('get', URL);
  handle400Statuses(status);
  if (status === 200) {
    const groupedData = groupData(data);
    dispatch({
      type: uploadStatusActions.receiveData,
      payload: {
        timeStart,
        data: groupedData,
      },
    });
  }
}

export function useFileProcessingStatus(id) {
  const state = useSelector((state) => state.uploadStatus);
  const isComplete = state.data.completed.find((d) => d.doc_id === id);
  const isProcessing = state.data.pending.find((d) => d.doc_id === id);
  const processingStatus = isComplete ? 'complete' : isProcessing ? 'processing' : null;
  return { processingStatus };
}

export function useUploadStatusTemplateGeneration() {
  const dispatch = useDispatch();
  const state = useSelector((state) => state.uploadStatus);

  React.useEffect(() => {
    fetchAndUpdateStoreTemplateGenerationUploadStatus(dispatch);
  }, []);

  React.useEffect(() => {
    let timeoutId;
    const schedule = () => {
      timeoutId = setTimeout(() => {
        fetchAndUpdateStoreTemplateGenerationUploadStatus(dispatch);
        schedule();
      }, POLL_DELAY);
    };
    schedule();
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  return state;
}

export function useUploadStatusDDQTemplateGeneration(onFileUploadComplete) {
  const dispatch = useDispatch();
  const state = useSelector((state) => state.uploadStatus);
  const previousCompletedDocIdsRef = React.useRef(null);
  const skippedCompletedSnapshotsRef = React.useRef(0);

  React.useEffect(() => {
    fetchAndUpdateStoreDDQTemplateGenerationUploadStatus(dispatch);
  }, []);

  React.useEffect(() => {
    let timeoutId;
    const schedule = () => {
      timeoutId = setTimeout(() => {
        fetchAndUpdateStoreDDQTemplateGenerationUploadStatus(dispatch);
        schedule();
      }, POLL_DELAY);
    };
    schedule();
    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  React.useEffect(() => {
    const completedDocs = state.data?.completed || [];
    const completedDocIds = new Set(completedDocs.map((doc) => doc.doc_id || doc.id));

    if (skippedCompletedSnapshotsRef.current < 2) {
      skippedCompletedSnapshotsRef.current += 1;
      previousCompletedDocIdsRef.current = completedDocIds;
      return;
    }

    completedDocs.forEach((doc) => {
      const docId = doc.doc_id || doc.id;
      if (!previousCompletedDocIdsRef.current.has(docId)) {
        onFileUploadComplete?.(doc);
      }
    });

    previousCompletedDocIdsRef.current = completedDocIds;
  }, [onFileUploadComplete, state.data]);

  return state;
}

export function useUploadStatus(filter) {
  const dispatch = useDispatch();
  const state = useSelector((state) => state.uploadStatus);

  React.useEffect(() => {
    const fileFilter = filter;
    fetchAndUpdateStoreUploadStatus(dispatch, fileFilter);
    const updater = () => {
      fetchAndUpdateStoreUploadStatus(dispatch, fileFilter);
    };

    const interval = setInterval(updater, POLL_DELAY);
    return () => {
      clearInterval(interval);
    };
  }, [filter]);

  return state;
}
